<script setup lang="ts">
import type { ComputedRef } from "vue"
import type { MediaTypeFilter } from "../GalleryFiltersDrawer/galleryFilters.interface"
import type { IMediaTableFilters } from "./mediaTableFilters.interface"
import type { ISelectItem } from "@/types"
import { computed, watch } from "vue"
import { useListAlbums } from "@/api"
import { Button, Combobox, Drawer, Select, Switch } from "@/components/ui"
import { useTranslation } from "@/composables"
import { MediaFiltersDrawerKey } from "@/symbols"

type SortByOption = "GuestName" | "Title" | "DateUploaded"
type OrderByOption = "asc" | "desc"

interface MediaTableFiltersProps {
  filters: IMediaTableFilters
  eventVaultId?: number
  eventTags?: string[]
  showModerationFilter?: boolean
  taggingEnabled?: boolean
}

interface ApprovalStatusOption extends ISelectItem<string> {
  description: string
}

const props = withDefaults(defineProps<MediaTableFiltersProps>(), {
  eventTags: () => [],
  showModerationFilter: true,
  taggingEnabled: false,
})

const emit = defineEmits<{
  (e: "update:filters", value: Partial<IMediaTableFilters>): void
}>()

const { t } = useTranslation()

function updateFilters(partial: Partial<IMediaTableFilters>) {
  emit("update:filters", partial)
}

const hasActiveFilters = computed(
  () =>
    props.filters.albumId
    || (props.showModerationFilter && props.filters.awaitingModeration === true)
    || props.filters.sortBy
    || props.filters.type?.length
    || props.filters.tags?.length
    || props.filters.showPinnedUploadsOnly,
)

function clearFilters() {
  updateFilters({
    albumId: undefined,
    awaitingModeration: false,
    sortBy: undefined,
    orderBy: undefined,
    type: [],
    tags: [],
    showPinnedUploadsOnly: false,
  })
}

const perPageOptions: ISelectItem[] = [
  { label: "5", value: 5 },
  { label: "10", value: 10 },
  { label: "25", value: 25 },
  { label: "50", value: 50 },
  { label: "100", value: 100 },
  { label: "250", value: 250 },
]

const sortByOptions: ISelectItem<SortByOption>[] = [
  { label: t("media_table_filters.sort_by_options.uploader"), value: "GuestName" },
  { label: t("media_table_filters.sort_by_options.image_name"), value: "Title" },
  { label: t("media_table_filters.sort_by_options.date_uploaded"), value: "DateUploaded" },
]

const orderByOptions = computed<ISelectItem<OrderByOption>[]>(() => {
  if (props.filters.sortBy === "DateUploaded") {
    return [
      { label: t("media_table_filters.order_by_options.newest_first"), value: "desc" },
      { label: t("media_table_filters.order_by_options.oldest_first"), value: "asc" },
    ]
  }
  return [
    { label: t("media_table_filters.order_by_options.ascending"), value: "asc" },
    { label: t("media_table_filters.order_by_options.descending"), value: "desc" },
  ]
})

const mediaTypeOptions = computed<ISelectItem<MediaTypeFilter>[]>(() => [
  { label: t("images"), value: "pics" },
  { label: t("videos"), value: "vids" },
  { label: t("guestbook_videos"), value: "guestbookVids" },
])

const tagSearch = defineModel<string>("tagSearch", { default: "" })

const tagOptions = computed<ISelectItem<string>[]>(() =>
  props.eventTags.map(tag => ({ label: tag, value: tag })),
)

const filteredTagOptions = computed<ISelectItem<string>[]>(() => {
  const normalizedSearch = tagSearch.value.trim().toLowerCase()

  if (!normalizedSearch)
    return tagOptions.value

  return tagOptions.value.filter(option => option.label.toLowerCase().includes(normalizedSearch))
})

const selectedTags = computed<ISelectItem<string>[]>({
  get: () => tagOptions.value.filter(option => props.filters.tags?.includes(option.value)),
  set: value => updateFilters({ tags: value.map(option => option.value) }),
})

const selectedMediaTypes = computed<ISelectItem<MediaTypeFilter>[]>({
  get: () => mediaTypeOptions.value.filter(option => props.filters.type?.includes(option.value)),
  set: (value) => {
    updateFilters({ type: value.map(option => option.value) })
  },
})

function onMediaTypeChange(value?: ISelectItem<MediaTypeFilter> | ISelectItem<MediaTypeFilter>[]) {
  if (!value) {
    updateFilters({ type: [] })
    return
  }

  const nextValues = Array.isArray(value) ? value : [value]
  updateFilters({ type: nextValues.map(option => option.value) })
}

const awaitingModerationOptions = computed<ApprovalStatusOption[]>(() => [
  {
    label: t("media_table_filters.approval_status.all"),
    value: "false",
    description: t("media_table_filters.approval_status.all_description"),
  },
  {
    label: t("media_table_filters.approval_status.waiting_approval"),
    value: "true",
    description: t("media_table_filters.approval_status.waiting_approval_description"),
  },
])

const { data: albums } = useListAlbums(props.eventVaultId)

const albumOptions: ComputedRef<ISelectItem[]> = computed(
  () => albums.value?.map(album => ({ label: album.title, value: album.id }) as ISelectItem) ?? [],
)

watch(
  () => props.filters.sortBy,
  (newVal) => {
    if (!newVal)
      updateFilters({ orderBy: undefined })
  },
)
</script>

<template>
  <Drawer :name="MediaFiltersDrawerKey" :title="t('media_table_filters.title')">
    <form @submit.prevent>
      <div class="flex flex-col gap-4">
        <Switch
          id="favouritesOnly"
          :model-value="filters.showPinnedUploadsOnly"
          name="favouritesOnly"
          :label="t('media_table_filters.favourites_only')"
          @update:model-value="(value: boolean) => updateFilters({ showPinnedUploadsOnly: value })"
        />

        <Select
          :model-value="filters.perPage"
          :items="perPageOptions"
          name="perPage"
          :label="t('media_table_filters.items_per_page')"
          @update:model-value="(value: number) => updateFilters({ perPage: value })"
        />
        <Select
          :model-value="filters.albumId"
          :items="albumOptions"
          name="albumId"
          :label="t('media_table_filters.album')"
          clearable
          @update:model-value="(value: number) => updateFilters({ albumId: value })"
        />

        <Combobox
          v-model="selectedMediaTypes"
          name="mediaType"
          :label="t('media_table_filters.media_type_label')"
          :items="mediaTypeOptions"
          multiple
          @update:model-value="onMediaTypeChange"
        />

        <Combobox
          v-if="taggingEnabled && eventTags.length"
          v-model="selectedTags"
          v-model:search="tagSearch"
          name="tags"
          :label="t('media_table_filters.tags_label')"
          :items="filteredTagOptions"
          multiple
          searchable
        />

        <Select
          :model-value="filters.sortBy"
          name="sortBy"
          :label="t('media_table_filters.sort_by_label')"
          :items="sortByOptions"
          :placeholder="t('media_table_filters.select_placeholder')"
          clearable
          @update:model-value="(value: SortByOption) => updateFilters({ sortBy: value })"
        />
        <Select
          :model-value="filters.orderBy"
          name="orderBy"
          :label="t('media_table_filters.order_by_label')"
          :items="orderByOptions"
          :placeholder="t('media_table_filters.select_placeholder')"
          clearable
          :disabled="!filters.sortBy"
          @update:model-value="(value: OrderByOption) => updateFilters({ orderBy: value })"
        />

        <Select
          v-if="showModerationFilter"
          :model-value="filters.awaitingModeration ? 'true' : 'false'"
          :items="awaitingModerationOptions"
          name="awaitingModeration"
          :label="t('media_table_filters.approval_status_label')"
          :hint="t('media_table_filters.approval_status_hint')"
          @update:model-value="(v: string) => updateFilters({ awaitingModeration: v === 'true' })"
        >
          <template #item="{ item }">
            <div class="flex flex-col gap-0.5 py-1">
              <span class="font-medium">{{ item.label }}</span>
              <span class="text-xs text-gray-500">{{ (item as ApprovalStatusOption).description }}</span>
            </div>
          </template>
        </Select>
      </div>
    </form>
    <template #footer>
      <div class="flex px-6 py-4">
        <Button prefix-icon="Close" variant="tertiary" class="w-full" :disabled="!hasActiveFilters" @click="clearFilters">
          {{ t('media_table_filters.clear_filters') }}
        </Button>
      </div>
    </template>
  </Drawer>
</template>
